import axios from 'axios'
// import qs from 'qs'
import { toast } from 'vue-sonner'
// import apiSystem from './modules/system'

// 请求重试配置
const MAX_RETRY_COUNT = 3 // 最大重试次数
const RETRY_DELAY = 1000 // 重试延迟时间（毫秒）

// 扩展 AxiosRequestConfig 类型
declare module 'axios' {
  export interface AxiosRequestConfig {
    _retry?: boolean
    __retryCount?: number
  }
}

const api = axios.create({
  baseURL: (import.meta.env.DEV && import.meta.env.VITE_OPEN_PROXY) ? '/proxy/' : import.meta.env.VITE_APP_API_BASEURL,
  timeout: 1000 * 60 * 2, // 请求超时时间，2分钟
  responseType: 'json',
  withCredentials: true, // 允许跨域请求携带Cookie
})

// 用户活动检测变量
// let lastActivityTime = Date.now()
// let INACTIVITY_TIMEOUT = 60 * 60 * 1000 // 默认60分钟无活动自动登出

// 获取登出时间设置
// export async function fetchLogoutTimeout() {
//   try {
//     const response = await apiSystem.getSettingByKey('login_auto_logout')
//     if (response.status === 1 && response.data && response.data.value) {
//       // 将分钟转换为毫秒
//       const timeoutMinutes = Number.parseInt(response.data.value, 10)
//       if (!Number.isNaN(timeoutMinutes) && timeoutMinutes > 0) {
//         INACTIVITY_TIMEOUT = timeoutMinutes * 60 * 1000
//         // 缓存登出时间设置
//         localStorage.setItem('autoLogoutTimeout', INACTIVITY_TIMEOUT.toString())
//         console.warn(`Auto logout time set to ${timeoutMinutes} minutes`)
//       }
//     }
//   }
//   catch (error) {
//     console.error('Failed to fetch auto logout timeout settings:', error)
//   }
// }

// 从缓存中恢复登出时间设置
// const cachedTimeout = localStorage.getItem('autoLogoutTimeout')
// if (cachedTimeout) {
//   const timeout = Number.parseInt(cachedTimeout, 10)
//   if (!Number.isNaN(timeout) && timeout > 0) {
//     INACTIVITY_TIMEOUT = timeout
//     console.warn(`从缓存恢复自动登出时间: ${INACTIVITY_TIMEOUT / 60 / 1000} 分钟`)
//   }
// }

// 更新最后活动时间
// function updateLastActivity() {
//   lastActivityTime = Date.now()
//   // 存储最后活动时间到localStorage，以便在多个标签页之间共享
//   localStorage.setItem('lastActivityTime', lastActivityTime.toString())
// }

// 添加用户活动监听器
// window.addEventListener('mousemove', updateLastActivity)
// window.addEventListener('keydown', updateLastActivity)
// window.addEventListener('click', updateLastActivity)
// window.addEventListener('scroll', updateLastActivity)

// 从其他标签页同步活动时间
// window.addEventListener('storage', (event) => {
//   if (event.key === 'lastActivityTime') {
//     lastActivityTime = Number.parseInt(event.newValue || Date.now().toString())
//   }
// })

// 定期检查用户活动状态
// setInterval(() => {
//   const userStore = useUserStore()
//   if (userStore.isLogin) {
//     // 从localStorage获取最新的活动时间（支持多标签页）
//     const storedTime = localStorage.getItem('lastActivityTime')
//     if (storedTime) {
//       lastActivityTime = Number.parseInt(storedTime)
//     }

//     if (Date.now() - lastActivityTime > INACTIVITY_TIMEOUT) {
//       // 用户长时间不活动，自动登出
//       userStore.requestLogout()
//       toast.info('Auto Logout', {
//         description: 'You have been automatically logged out due to inactivity',
//       })
//     }
//   }
// }, 60000) // 每分钟检查一次

api.interceptors.request.use(
  (request) => {
    // 全局拦截请求发送前提交的参数
    const userStore = useUserStore()
    // 获取当前语言设置
    const currentLang = localStorage.getItem('lang') || 'zh-cn'
    // 设置请求头
    if (request.headers) {
      // 添加语言请求头
      request.headers['Accept-Language'] = currentLang
      // 添加CSRF保护头
      request.headers['X-Requested-With'] = 'XMLHttpRequest'
      // 仍然发送Token以兼容旧版API，但主要依赖HttpOnly Cookie
      if (userStore.isLogin) {
        const token = localStorage.getItem('token')
        if (token) {
          request.headers.Authorization = `Bearer ${token}`
        }
      }
    }
    return request
  },
)

api.interceptors.response.use(
  (response) => {
    /**
     * 全局拦截请求发送后返回的数据，如果数据有报错则在这做全局的错误提示
     * 假设返回数据格式为：{ status: 1, error: '', data: {} }
     * 规则是当 status 为 1 时表示请求成功，为 0 时表示接口需要登录或者登录状态失效，需要重新登录
     * 请求出错时 error 会返回错误信息
     */
    if (response.data.status === 1) {
      if (response.data.error !== '') {
        toast.warning('Warning', {
          description: response.data.error,
        })
        return Promise.reject(response.data)
      }
    }
    return Promise.resolve(response.data)
  },
  async (error) => {
    // 获取请求配置
    const config = error.config
    const originalRequest = config
    const userStore = useUserStore()

    // 标记重试相关字段的默认值
    if (originalRequest && originalRequest._retry == null) {
      originalRequest._retry = false
    }

    // 处理请求超时（Axios 超时通常会带有 code === 'ECONNABORTED' 或 message 中包含 'timeout'）
    if (error.code === 'ECONNABORTED' || (error.message && error.message.toLowerCase().includes('timeout'))) {
      if (config) {
        config.__retryCount = config.__retryCount || 0
        if (config.__retryCount < MAX_RETRY_COUNT) {
          config.__retryCount += 1
          // 延迟后重试
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
          return api(config)
        }
      }

      // 超时且已达重试上限，给用户友好提示并返回超时错误
      toast.error('请求超时', {
        description: '请求响应超时，请检查网络或稍后重试。',
      })
      return Promise.reject(error)
    }

    // 没有 response（断网或跨域）
    if (!error.response) {
      // 对于无 response 的情况，可能是网络断开、CORS 或其他原因，给出提示
      toast.error('网络错误', {
        description: '无法连接到服务器，请检查网络或稍后重试。',
      })
      return Promise.reject(error)
    }

    const status = error.response.status

    if (status === 401) {
      if (originalRequest.url.includes('refresh')) {
        await userStore.requestLogout()
        return Promise.reject(error)
      }

      // 没有 accessToken，不能刷新，直接登出
      if (!userStore.token) {
        await userStore.requestLogout()
        return Promise.reject(error)
      }

      // 如果本次请求已经重试过，不再重复刷新（防止循环）
      if (originalRequest._retry) {
        await userStore.requestLogout()
        return Promise.reject(error)
      }

      // 标记本次请求为重试请求
      originalRequest._retry = true

      try {
        // 刷新 accessToken
        await userStore.refreshToken()

        // 用新 token 重试原请求
        if (config && userStore.token) {
          config.headers = config.headers || {}
          config.headers.Authorization = `Bearer ${userStore.token}`
          return api(config)
        }
      }
      catch (err) {
        await userStore.requestLogout()
        return Promise.reject(err)
      }
    }

    // 非 401 的普通重试逻辑
    if (config) {
      // 设置重试次数
      config.__retryCount = config.__retryCount || 0
      // 判断是否超过重试次数
      const shouldRetry = (status >= 500 && config.__retryCount < MAX_RETRY_COUNT)
      if (shouldRetry) {
        // 重试次数自增
        config.__retryCount += 1
        // 延迟重试
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
        // 重新发起请求
        return api(config)
      }
    }

    return Promise.reject(error)
  },
)

export default api
